import { Modal, Alert, Button, Col, Row, Space, Typography, message } from "antd";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import Loader from "../controls/Loader";
import ReceiptsView from "../components/ReceiptsView";
import { getNotificationRecipientsExport } from "../repository/data.repository";
import { getProblemMessage } from "../utils/http";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";

function ReceiptsPage() {
  const permissions = useSelector(authorizeSelectors.permissions);
  const location = useLocation();
  const navState = location.state as {
    notificationUid?: string;
    notificationTitle?: string;
  } | null;

  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  // Drill from the All grid: lock the receipts to one notification (clearable).
  const [notificationUid, setNotificationUid] = useState<string | undefined>(
    navState?.notificationUid,
  );

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading receipts export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    // H3: when drilled into one notification, scope the export too — otherwise exporting dumps every
    // receipt in the tenant and silently answers a different question than the grid is showing.
    const effectiveSearchQuery: SearchQuery = notificationUid
      ? {
          conditions: [
            ...searchQuery.conditions,
            {
              field: "notificationUid",
              operation: QueryOperation.Equal,
              values: [notificationUid],
            },
          ],
        }
      : searchQuery;
    getNotificationRecipientsExport({
      searchQuery: effectiveSearchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download receipts export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Receipts
        </Typography.Title>
        <Space className="flex flex-row">
          <Button
            type="primary"
            disabled={!permissions.can("NotificationsRecipients", "view")}
            onClick={onDownloadExport}
          >
            Export to Excel
          </Button>
        </Space>
      </Row>
      {notificationUid && (
        <Row className="px-4 pb-2">
          <Alert
            className="w-full"
            type="info"
            showIcon
            message={`Showing receipts for notification "${navState?.notificationTitle ?? ""}"`}
            action={
              <Button size="small" onClick={() => setNotificationUid(undefined)}>
                Clear
              </Button>
            }
          />
        </Row>
      )}
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <ReceiptsView
          key={notificationUid ?? "all"}
          notificationUid={notificationUid}
          onSearch={onSearch}
        />
      </Col>
      {contextHolder}
    </Col>
  );
}

export default ReceiptsPage;
